function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useRef, useState, useContext, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '../../../Button';
import { Chip } from '../../../Chip';
import { Close } from '../../../Close';
import { Tag } from '../../../Tag';
import { Tooltip } from '../../../Tooltip';
import { ENTER_KEY, SPACE_KEYCODE } from '../../../Utils/constant';
import { isHeaderOverlayOpen } from '../../../Utils/helper';
import { SelectMenuContext } from '../../context';
import { SelectMenuTypes, removePlaceholder, defaultSelectedValue } from '../../helper';
import styles from '@dtsl/css-select-menu/dist/index.module.css';
const SelectMenuControl = props => {
  const ref = useRef(null);
  const contentPrimaryInfoRef = useRef(null);
  const {
    isOpen,
    setIsOpen,
    isOverlayOpen,
    setIsOverlayOpen,
    selectedValues,
    setSelectedValues,
    isMobile,
    searchRef,
    searchValue,
    handleSearch,
    isControlSearchable,
    isPanelSearchable,
    selectMenuControlRef,
    isSearchable,
    floatingUiProps,
    onClose,
    selectMenuRef,
    setSelectMenuWidth
  } = useContext(SelectMenuContext);
  const {
    readonly,
    disabled,
    tooltipData,
    selectMenuType,
    placeHolder,
    arrowClassName,
    isControlButton,
    controlClassName,
    placeholderClassName,
    controlButtonMetaData,
    dataTestId,
    maxTagWidth,
    countTagWidth,
    isControlChip,
    controlChipMetaData,
    onClick,
    isSelectionClearable,
    identifier,
    size,
    onFocus,
    onBlur,
    creatableButtonMetaData,
    isCreatable,
    isExactMatch,
    options,
    isError
  } = props;
  const MAX_TAG_WIDTH = maxTagWidth || 100;
  const COUNT_TAG_WIDTH = countTagWidth || 75;
  const filteredSelectedValues = removePlaceholder(selectedValues, placeHolder);
  const filteredSelectedValuesCount = filteredSelectedValues.length;
  const [noOfTags, setNoOfTags] = useState(0);
  const [visible, setVisible] = useState(false);
  const [controlTooltipVisible, setControlTooltipVisible] = useState(false);
  const isOpenRef = React.useRef(isMobile ? isOverlayOpen : isOpen);
  isOpenRef.current = isMobile ? isOverlayOpen : isOpen;
  const tagsContainerRef = useRef(null);
  const {
    text: tooltipText,
    timeout = 200,
    placement = 'bottom',
    offset: tooltipOffset = [0, 8]
  } = tooltipData || {};
  const calculateNoofTags = () => {
    if (ref?.current && ref?.current?.offsetWidth) {
      const totalWidth = ref.current.offsetWidth;
      let placeholderWidth = isControlSearchable ? 105 : 0;
      const tagsContainer = tagsContainerRef?.current;
      if (tagsContainer) {
        if (isControlSearchable) {
          if (ref?.current?.children?.[1]?.offsetWidth < 89) {
            // handling the case when width of the searchable text area (div having class 'input-wrapper')
            // shrinks below its minimum allowed width of 89px.
            tagsContainer.style.maxWidth = `${COUNT_TAG_WIDTH}px`;
            if (isControlSearchable) {
              placeholderWidth = totalWidth <= COUNT_TAG_WIDTH ? 0 : totalWidth - COUNT_TAG_WIDTH;
            }
          } else {
            tagsContainer.style.maxWidth = `${totalWidth - 105}px`;
          }
        } else {
          tagsContainer.style.maxWidth = `${totalWidth}px`;
        }
      }
      const totalNoOfTags = Math.floor((totalWidth - COUNT_TAG_WIDTH - placeholderWidth) / MAX_TAG_WIDTH);
      const correctedTotalTags = totalNoOfTags > 0 ? totalNoOfTags : 0;
      setNoOfTags(correctedTotalTags);
    }
  };
  useEffect(() => {
    calculateNoofTags();
    const controlRegion = ref?.current;
    if (!controlRegion) return;
    const resizeObserver = new ResizeObserver(() => {
      if (ref.current) {
        calculateNoofTags();
        setSelectMenuWidth(selectMenuRef?.current?.offsetWidth);
      }
    });
    resizeObserver.observe(controlRegion); // update count of tags that can be
    // accommodated, when the control region resizes dynamically
    return () => {
      // Cleanup the observer by unobserving all elements
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (isSearchable && isOpen) {
      searchRef.current?.focus();
      selectMenuControlRef.current?.classList.add(styles['sib-selectmenu-control-focused']);
    }
  }, [controlTooltipVisible]);
  useEffect(() => {
    if (isMobile) {
      return;
    }
    const updateVisible = element => {
      if (!element) {
        return;
      }
      if (element.offsetWidth < element.scrollWidth && !isOpen) {
        setControlTooltipVisible(true);
        return;
      }
      setVisible(false);
      setControlTooltipVisible(false);
    };
    updateVisible(contentPrimaryInfoRef.current || ref.current);
  }, [isMobile, isOpen]);
  const isValueSelected = () => {
    return (selectedValues[0] || {}).value !== '';
  };
  const isCrossButtonVisible = () => {
    let isCrossVisible = false;
    if (selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION) {
      const {
        label
      } = selectedValues[0];
      isCrossVisible = label !== placeHolder ? true : false;
    } else {
      isCrossVisible = selectedValues.length > 1 ? true : false;
    }
    return isCrossVisible && isSelectionClearable && !readonly && !disabled; // DO not show cross button for readonly and disabled state
  };
  const controlClass = classNames({
    [styles['sib-selectmenu-control']]: true,
    [styles['sib-selectmenu-control--small']]: size === 'small',
    [styles['sib-selectmenu-control--readonly']]: readonly,
    [styles['sib-selectmenu-disabled']]: disabled,
    [controlClassName]: !!controlClassName,
    [styles['sib-selectmenu-control--with-cross-btn']]: isCrossButtonVisible()
  });
  const placeholderClass = classNames({
    [styles['is-selected']]: isValueSelected(),
    [styles['sib-selectmenu-control__placeholder']]: true,
    [styles['search__placeholder--multiSelectOption']]: selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION,
    [styles['search__placeholder--singleSelectOption']]: selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION,
    [placeholderClassName]: !!placeholderClassName
  });
  const arrowClass = `${styles['sib-selectmenu-arrow']} ${arrowClassName}`;
  const {
    handleCreateOption
  } = creatableButtonMetaData;
  const removeTag = (event, tag) => {
    event.stopPropagation();
    event.preventDefault();
    const filteredValues = selectedValues.filter(selectedValue => selectedValue.label !== tag.label);
    setSelectedValues(filteredValues.length ? [...filteredValues] : [defaultSelectedValue(placeHolder)]);
    props.removeTag(tag, [...filteredValues]);
    if (isOpen || isOverlayOpen) {
      searchRef.current?.focus();
      selectMenuControlRef.current?.classList.add(styles['sib-selectmenu-control-focused']);
    }
  };
  const handleSelectionClear = () => {
    setSelectedValues([defaultSelectedValue(placeHolder)]);
    props.onSelectionClear();
  };
  const handleCrossClick = event => {
    event.stopPropagation();
    handleSelectionClear();
  };
  const handleKeyDownCrossClick = event => {
    if (event.key === ENTER_KEY || event.keyCode === SPACE_KEYCODE) {
      event.stopPropagation();
      handleSelectionClear();
    }
  };
  const showCreatableListPanel = isCreatable && (searchValue || options?.length) || !isCreatable;
  const handleFocusOut = () => {
    if (ref?.current) {
      selectMenuControlRef.current?.classList?.remove(styles['sib-selectmenu-control-focused']);
    }
  };
  const handleOnClick = event => {
    // event.stopPropagation();
    event.preventDefault();
    if (!disabled) {
      setVisible(false);
      if (isMobile) {
        if (isHeaderOverlayOpen() && !isOverlayOpen) {
          setTimeout(() => {
            if (!isOverlayOpen) {
              searchValue && handleSearch('');
            }
            setIsOverlayOpen(!isOverlayOpen);
          }, 500);
        } else {
          if (!isOverlayOpen) {
            searchValue && handleSearch('');
          }
          showCreatableListPanel ? setIsOverlayOpen(!isOverlayOpen) : setIsOverlayOpen(false);
        }
      } else {
        if (!isOpen) {
          searchValue && handleSearch('');
        }
        showCreatableListPanel ? setIsOpen(!isOpen) : setIsOpen(false);
      }
      if (ref?.current && !isOpen) {
        selectMenuControlRef.current?.classList.add(styles['sib-selectmenu-control-focused']);
      }
      onClick(event, isMobile ? isOverlayOpen : isOpen);
    }
  };
  const handleKeyDown = event => {
    if (!isCreatable) {
      if (event.key === ENTER_KEY || event.keyCode === SPACE_KEYCODE && !isControlSearchable) {
        onClose(event, isMobile ? isOverlayOpen : isOpen);
        isMobile ? setIsOverlayOpen(!isOverlayOpen) : setIsOpen(!isOpen);
      }
    } else if (event.key === ENTER_KEY && !isExactMatch) {
      handleCreateOption(searchValue, event);
      handleSearch('');
    }
    if (event.keyCode === 40) {
      if (isMobile) {
        showCreatableListPanel ? setIsOverlayOpen(!isOverlayOpen) : setIsOverlayOpen(false);
      } else {
        showCreatableListPanel ? setIsOpen(!isOpen) : setIsOpen(false);
      }
    }
  };
  useEffect(() => {
    if (isCreatable && !searchValue && !options.length) {
      isMobile ? setIsOverlayOpen(false) : setIsOpen(false);
    }
  }, [isCreatable, searchValue, options, isMobile]);
  const getPlaceHolderValue = () => {
    const tagsContainerClass = classNames({
      [styles['sib-tags-selectmenu']]: true,
      [styles['sib-tags-selectmenu--nosearch']]: !isControlSearchable
    });
    if (selectedValues.length > 0) {
      const {
        addtionalInfo = () => {},
        label,
        altLabel,
        svg
      } = selectedValues[0];
      const addInfo = selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION ? addtionalInfo() : '';
      if (selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) {
        if (selectedValues.length === 1 && selectedValues[0].label === placeHolder && (!searchValue || isPanelSearchable) && !isControlSearchable) {
          return selectedValues[0].label;
        }
        return /*#__PURE__*/React.createElement("div", {
          className: tagsContainerClass,
          ref: tagsContainerRef
        }, filteredSelectedValues.slice(0, noOfTags).map(selectedValue => {
          const {
            label,
            altLabel
          } = selectedValue;
          const tagClass = classNames({
            [styles['sib-tag-selectmenu']]: true,
            [styles['sib-tag-selectmenu-new']]: true
          });
          return /*#__PURE__*/React.createElement(Tag, {
            key: `${selectedValue.key}_${selectedValue.label}`,
            label: altLabel ? altLabel : label,
            closable: !(readonly || disabled) // DO not show cross button for readonly and disabled state
            ,
            onClose: event => removeTag(event, selectedValue),
            className: tagClass,
            styles: {
              minWidth: `${MAX_TAG_WIDTH}px`
            },
            showTooltip: true,
            disabled: disabled || readonly
          });
        }), filteredSelectedValuesCount > noOfTags ? /*#__PURE__*/React.createElement(Tag, {
          label: `+${filteredSelectedValuesCount - noOfTags}`,
          disabled: disabled || readonly
        }) : null);
      }
      if ((selectMenuType === SelectMenuTypes.SINGLE_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_SINGLE_SELECTION) && searchValue && !isPanelSearchable) {
        return;
      }
      return /*#__PURE__*/React.createElement("div", {
        className: styles['sib-selectmenu--selected-value']
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['primary-info'],
        ref: contentPrimaryInfoRef
      }, svg && /*#__PURE__*/React.cloneElement(svg, {
        className: styles['primary-info__icon']
      }), altLabel ? altLabel : label), addInfo && /*#__PURE__*/React.createElement("span", {
        className: styles['additional-info']
      }, addInfo));
    }
  };
  const {
    refs
  } = floatingUiProps;
  const getControlTrigger = () => {
    if (isControlButton) {
      return /*#__PURE__*/React.createElement("div", {
        ref: selectMenuControlRef
      }, /*#__PURE__*/React.createElement(Button, _extends({}, controlButtonMetaData, {
        dropdownOpen: isMobile ? isOverlayOpen : isOpen,
        onClick: !readonly ? handleOnClick : () => {},
        disabled: disabled,
        ref: refs.setReference,
        isFloatingComp: true
      })));
    }
    if (isControlChip) {
      return /*#__PURE__*/React.createElement("div", {
        ref: selectMenuControlRef
      }, /*#__PURE__*/React.createElement(Chip, _extends({}, controlChipMetaData, {
        variant: "selectableWithPanel",
        dropdownOpen: isMobile ? isOverlayOpen : isOpen,
        onClick: !readonly ? handleOnClick : () => {},
        disabled: disabled,
        ref: refs.setReference
      })));
    }
  };
  const validationTextId = isError ? `${identifier}-validationText` : '';
  const selectMenuControl = !isControlButton && !isControlChip ? /*#__PURE__*/React.createElement("div", {
    ref: refs.setReference
  }, /*#__PURE__*/React.createElement("div", {
    ref: selectMenuControlRef,
    disabled: disabled,
    className: controlClass,
    onClick: !readonly ? handleOnClick : () => {},
    onKeyDown: !readonly ? handleKeyDown : () => {},
    onBlur: handleFocusOut,
    "aria-haspopup": "listbox",
    "data-testid": dataTestId ? dataTestId : null,
    tabIndex: !disabled ? '0' : null,
    role: "button"
  }, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: placeholderClass
  }, getPlaceHolderValue(), isControlSearchable && /*#__PURE__*/React.createElement("div", {
    className: styles['input-wrapper'],
    "data-value": searchValue
  }, /*#__PURE__*/React.createElement("input", {
    readOnly: readonly,
    ref: searchRef,
    id: identifier,
    placeholder: (selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) && selectedValues.length === 1 && selectedValues[0].label === placeHolder ? placeHolder : '',
    className: `
                  ${(selectMenuType === SelectMenuTypes.MULTI_SELECTION || selectMenuType === SelectMenuTypes.FOLDER_MULTI_SELECTION) && selectedValues.length > 1 ? styles['offset-input'] : ''}`,
    value: searchValue || '',
    onChange: event => {
      const searchedValue = event.target.value;
      handleSearch(searchedValue);
    },
    onFocus: event => {
      event.stopPropagation();
      selectMenuControlRef.current?.classList.add(styles['sib-selectmenu-control-focused']);
      onFocus();
    },
    onBlur: event => {
      event.stopPropagation();
      selectMenuControlRef.current?.classList.remove(styles['sib-selectmenu-control-focused']);
      onBlur();
    },
    "aria-describedby": `${identifier}-specificationText ${validationTextId}`
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles['sib-selectmenu-arrow-wrapper']
  }, isCrossButtonVisible() && /*#__PURE__*/React.createElement("div", {
    className: styles['sib-selectmenu-cross']
  }, /*#__PURE__*/React.createElement(Close, {
    size: "small",
    onClick: handleCrossClick,
    onKeyDown: handleKeyDownCrossClick
  })), /*#__PURE__*/React.createElement("span", {
    className: arrowClass
  })))) : getControlTrigger();
  if (isMobile) {
    return selectMenuControl;
  }
  if (tooltipText && isControlButton) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      text: tooltipText,
      offset: tooltipOffset,
      visible: !isOpenRef.current && visible,
      placement: placement,
      onMouseEnter: () => {
        setTimeout(() => {
          if (!isOpenRef.current) {
            setVisible(true);
          }
        }, timeout);
      },
      onMouseLeave: () => {
        setTimeout(() => {
          setVisible(false);
        }, timeout);
      }
    }, selectMenuControl);
  }
  if (controlTooltipVisible) {
    if (contentPrimaryInfoRef.current) {
      return /*#__PURE__*/React.createElement(Tooltip, {
        text: contentPrimaryInfoRef.current.innerText
      }, selectMenuControl);
    } else {
      return /*#__PURE__*/React.createElement(Tooltip, {
        text: getPlaceHolderValue()
      }, selectMenuControl);
    }
  }
  return selectMenuControl;
};
SelectMenuControl.propTypes = {
  maxTagWidth: PropTypes.number,
  countTagWidth: PropTypes.number,
  tooltipData: PropTypes.object.isRequired,
  readonly: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  removeTag: PropTypes.func.isRequired,
  placeholderClassName: PropTypes.string.isRequired,
  selectMenuType: PropTypes.string.isRequired,
  placeHolder: PropTypes.string.isRequired,
  controlClassName: PropTypes.string.isRequired,
  arrowClassName: PropTypes.string.isRequired,
  isControlButton: PropTypes.bool.isRequired,
  controlButtonMetaData: PropTypes.object.isRequired,
  dataTestId: PropTypes.string,
  isControlChip: PropTypes.bool,
  isSelectionClearable: PropTypes.bool,
  controlChipMetaData: PropTypes.object,
  onClick: PropTypes.func,
  onSelectionClear: PropTypes.func,
  identifier: PropTypes.string,
  size: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  creatableButtonMetaData: PropTypes.object,
  isCreatable: PropTypes.bool,
  isExactMatch: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    value: PropTypes.any,
    icon: PropTypes.func,
    helpText: PropTypes.string,
    addtionalInfo: PropTypes.func,
    customLabelStyle: PropTypes.object
  })),
  isError: PropTypes.bool.isRequired
};
export default SelectMenuControl;